import { useCallback, useEffect, useState } from 'react'
import { core } from '@musinsa/pbo-core'
import { EtcOutboundDuplicateItem } from '@/services/outbound/outboundEtc/types'
import { MESSAGES } from '@/pages/outbound/outboundEtc/constants'

interface Props {
  open: boolean
  duplicateCount: number
  cancelCount: number
  items: EtcOutboundDuplicateItem[]
  /** 강행 저장 — 입력받은 사유를 subData 의 REMARK 로 실어 보낸다. */
  onProceed: (remark: string) => void
  onCancel: () => void
}

const COLUMNS: core.CoreColumnType[] = [
  { id: 'STATUS', title: '구분', dataIndex: 'STATUS', width: 100 },
  { id: 'ORD_NO', title: '주문번호', dataIndex: 'ORD_NO', width: 180 },
  { id: 'ORD_OPT_NO', title: '주문일련번호', dataIndex: 'ORD_OPT_NO', width: 190 },
  { id: 'BARCODE', title: '바코드', dataIndex: 'BARCODE', width: 160 },
  { id: 'STL_NO', title: '스타일넘버', dataIndex: 'STL_NO', width: 140 },
  { id: 'GDS_OPT', title: '옵션', dataIndex: 'GDS_OPT', width: 100 },
  { id: 'ORD_QTY', title: '주문수량', dataIndex: 'ORD_QTY', width: 100, align: 'right' },
  { id: 'ORD_STATUS', title: '주문상태', dataIndex: 'ORD_STATUS', width: 120 },
]

/**
 * 중복·취소 주문 확인 모달. 1.0 `DupCheckPopup` 동치.
 *
 * 저장 요청이 400 + `dupCheck` 로 돌아오면 뜬다. 진행을 누르면 서버가 되돌려준 `saveData` 를
 * `itemData` 로, 사유를 `subData[0].REMARK` 로 실어 같은 저장 API 를 다시 부른다. BE 는 `itemData`
 * 가 있으면 중복 검사를 건너뛰고(`skipDuplicateCheck`) 사유를 출고 코멘트 앞에 붙인다.
 *
 * 1.0 은 사유를 `window.prompt` 로 받았는데 브라우저 모달은 자동화·접근성이 모두 막혀 있어
 * 모달 안 입력으로 옮겼다. 사유 필수 규칙은 그대로다.
 */
export function DuplicateOrderModal({ open, duplicateCount, cancelCount, items, onProceed, onCancel }: Readonly<Props>) {
  const [remark, setRemark] = useState('')

  useEffect(() => {
    if (open) setRemark('')
  }, [open])

  const handleOk = useCallback(() => {
    if (!remark.trim()) return
    onProceed(remark.trim())
  }, [remark, onProceed])

  return (
    <core.Modal
      open={open}
      title="기타출고 확인"
      width={1040}
      okText="네, 진행합니다"
      cancelText="아니오"
      okButtonProps={{ disabled: !remark.trim() }}
      onOk={handleOk}
      onCancel={onCancel}
    >
      <core.Flex vertical gap={12} align="stretch">
        <core.Typography.Text>
          지금 등록하신 기타 출고에 출고하지 말아야 할 주문이 포함되어 있습니다. 그럼에도 출고 작업을 진행하시겠습니까?
        </core.Typography.Text>
        <core.Space size={16}>
          <core.Typography.Text strong>{`중복 주문번호 ${duplicateCount}건`}</core.Typography.Text>
          <core.Typography.Text strong>{`취소 주문번호 ${cancelCount}건`}</core.Typography.Text>
        </core.Space>
        <core.Table
          columns={COLUMNS}
          dataSource={items.map((item, index) => ({ ...item, key: `${item.ORD_NO}-${item.ORD_OPT_NO}-${index}` }))}
          rowKey="key"
          size="small"
          pagination={false}
          scrollY={280}
          cellSelection={false}
        />
        <core.Flex vertical gap={4}>
          <core.Typography.Text>
            사유 <span style={{ color: '#ff4d4f' }}>*</span>
          </core.Typography.Text>
          <core.Input placeholder="사유를 입력해주세요." value={remark} onChange={(e) => setRemark(e.target.value)} />
          <core.Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {MESSAGES.duplicateReasonRequired}
          </core.Typography.Text>
        </core.Flex>
      </core.Flex>
    </core.Modal>
  )
}
